import React, { useState, useEffect, useCallback } from 'react';
import { getStudentRepertoire, removeRepertoireEntry, restoreRepertoireEntry } from '../../services/RepertoireService';
import { canManageSongContent } from '../../utils/permissions';
import {
	fetchNotesForStudent,
	updateSongNoteText,
	deleteSongNote,
	setSongNoteStatus,
} from '../../services/SongNotesService';
import {
	partitionNotes,
	mapLegacyStatus,
	isDelivered,
	isLegalTransition,
	isDeliveryTransition,
	displaySongName,
	LEGAL_TRANSITIONS,
	SONG_NOTE_STATUS,
	WARMUPS_SONG_ID,
} from '../../services/songNotesModel';
import { useAuth } from '../../contexts/AuthContext/AuthContext';
import LinkSongControl from './LinkSongControl';
import TrackPills from '../Tracks/TrackPills';
import useTrackPlayer from '../Tracks/useTrackPlayer';
import useStudentTracks from '../Tracks/useStudentTracks';
import { removedTrackFiles, staffTrackLabel, trackDocsForSong, tracksForSong, unmatchedTrackDocs } from '../../services/studentTracksModel';
import { restoreTrackFile } from '../../services/StudentTracksService';
import TrackEditor, { EditTracksButton } from '../Tracks/TrackEditor';
import TracksOnFile from '../Tracks/TracksOnFile';
import './StudentRepertoire.css';

const { CAPTURED, FOCUS } = SONG_NOTE_STATUS;

// Sessions are counted, not stored: one distinct lessonId is one session. This
// derives correctly even for legacy docs whose sessions[] still hold the old
// per-autosave duplicate snapshots (many entries sharing one lessonId).
const sessionCount = (entry) => {
	const ids = (entry.sessions || []).map((s) => s.lessonId).filter(Boolean);
	return new Set(ids).size || 1;
};

/** Human labels for the two delivered states, used by the move buttons. */
const STATE_LABEL = {
	[SONG_NOTE_STATUS.CAPTURED]: 'Only you',
	[FOCUS]: 'Focus',
	[SONG_NOTE_STATUS.LANDED]: 'Landed',
};

/**
 * Song Notes for one song, rendered from the student-wide read the parent
 * holds (one `fetchNotesForStudent` per profile open, not one query per
 * expanded row — and the only way a note whose song has NO repertoire row can
 * be reached at all; see "Warm-ups & other notes" below).
 *
 * STAFF SURFACE. StudentRepertoire renders inside the employee portal, so
 * `captured` notes are shown — and labelled, so a teacher is never in doubt about
 * what the family can see. A customer-facing reuse must filter to delivered
 * notes in memory off fetchDeliveredNotesForStudent (the per-song delivered
 * query was deleted 2026-08-06 — its index never existed; see SongNotesService).
 *
 * THIS IS THE NON-LESSON WRITE SURFACE. The C decision (Anthony, 2026-08-06)
 * allowed EDIT TEXT and DELETE here and kept status transitions on the Lesson
 * Dashboard so delivery kept its in-lesson ceremony. **Anthony reversed the
 * status half on 2026-09-03**: a note's state must be changeable from the
 * profile too, so the legal moves from songNotesModel are offered here. What
 * is preserved from the original reasoning is the WALL: a move that crosses
 * captured → delivered asks first, in the Deliver dialog's own words, because
 * it is the one transition that changes who can see the note — and nothing
 * here (or anywhere) offers a way back to `captured`.
 * Delete semantics mirror the lesson surface: a `captured` note deletes freely
 * (nobody ever saw it); a delivered note asks first, because the family may
 * have read it.
 */
const SongNoteHistory = ({
	notes,
	loading = false,
	label = 'Song Notes',
	studentName = '',
	byTeacher = null,
	onPatch,
	onRemove,
	canManage = false, // edit + delete are admin / owner only (2026-09-27)
}) => {
	const [editingId, setEditingId] = useState(null);
	const [draft, setDraft] = useState('');
	// One confirm open at a time: { id, kind: 'delete' } | { id, kind: 'deliver', to }
	const [confirming, setConfirming] = useState(null);
	const [actionError, setActionError] = useState('');

	const startEdit = (note) => {
		if (!canManage) return;
		setActionError('');
		setConfirming(null);
		setEditingId(note.id);
		setDraft(note.text);
	};

	const saveEdit = async (note) => {
		const text = draft.trim();
		if (!text || text === note.text) {
			setEditingId(null);
			return;
		}
		try {
			await updateSongNoteText(note.id, text);
			onPatch(note.id, { text });
			setEditingId(null);
		} catch (err) {
			console.error('Error editing Song Note:', err);
			setActionError('That edit did not save. Try again.');
		}
	};

	// `to` is only compared when given: the render asks "is a delivery confirm
	// open on this row?", the click asks "is it THIS delivery?".
	const isConfirming = (note, kind, to = null) =>
		confirming?.id === note.id && confirming.kind === kind && (to == null || confirming.to === to);

	const removeNote = async (note) => {
		if (!canManage) return;
		// Delivered notes ask first — the family may have read them.
		if (isDelivered(note.status) && !isConfirming(note, 'delete')) {
			setActionError('');
			setConfirming({ id: note.id, kind: 'delete' });
			return;
		}
		try {
			await deleteSongNote(note.id);
			onRemove(note.id);
			setConfirming(null);
		} catch (err) {
			console.error('Error deleting Song Note:', err);
			setActionError('Could not delete that note.');
		}
	};

	/**
	 * Move a note between states. Illegal moves are never rendered, and the
	 * model refuses them anyway. Crossing the visibility wall asks first.
	 */
	const moveNote = async (note, to) => {
		const from = note.status;
		if (!isLegalTransition(from, to)) return;
		if (isDeliveryTransition(from, to) && !isConfirming(note, 'deliver', to)) {
			setActionError('');
			setConfirming({ id: note.id, kind: 'deliver', to });
			return;
		}
		try {
			const result = await setSongNoteStatus(note.id, { from, to, lessonId: null, byTeacher });
			onPatch(note.id, { status: to, statusChangedAt: result?.at || new Date() });
			setConfirming(null);
		} catch (err) {
			console.error('Error updating Song Note status:', err);
			setActionError('Status did not save. Try again.');
		}
	};

	const moveLabel = (from, to) => {
		if (isDeliveryTransition(from, to)) return `Deliver as ${STATE_LABEL[to]}`;
		return to === SONG_NOTE_STATUS.LANDED ? 'Mark landed' : 'Back to focus';
	};

	if (loading) {
		return (
			<div className="song-note-history">
				<div className="history-label">{label}</div>
				<p className="song-note-empty">
					<i className="fa-solid fa-spinner fa-spin"></i> Loading…
				</p>
			</div>
		);
	}

	if (!notes || notes.length === 0) return null;

	const { open, landed } = partitionNotes(notes);
	const who = studentName ? `${studentName}'s family` : 'the family';

	const renderNote = (note) => (
		<li key={note.id} className={`note-status-${note.status}`}>
			{note.status === CAPTURED && <i className="fa-solid fa-lock" title="Teacher-only"></i>}
			{note.status === FOCUS && <i className="fa-solid fa-star"></i>}
			{note.status === SONG_NOTE_STATUS.LANDED && <i className="fa-solid fa-circle-check"></i>}
			{editingId === note.id ? (
				<span className="note-edit">
					<input
						className="note-edit__input"
						value={draft}
						onChange={(e) => setDraft(e.target.value)}
						onKeyDown={(e) => {
							if (e.key === 'Enter') saveEdit(note);
							if (e.key === 'Escape') setEditingId(null);
						}}
						aria-label="Edit note text"
						autoFocus
					/>
					<button className="note-action" title="Save" onClick={() => saveEdit(note)}>
						<i className="fa-solid fa-check"></i>
					</button>
					<button className="note-action" title="Cancel" onClick={() => setEditingId(null)}>
						<i className="fa-solid fa-xmark"></i>
					</button>
				</span>
			) : (
				<>
					<span className="note-text">{note.text}</span>
					<span className="note-actions">
						<span className="note-state">{STATE_LABEL[note.status] || STATE_LABEL[CAPTURED]}</span>
						{isConfirming(note, 'deliver') ? (
							<span className="note-confirm" role="alert">
								Right now only teachers can see this. Deliver it to {who} as{' '}
								{STATE_LABEL[confirming.to]}?
								<button
									className="note-move note-move--go"
									title="Deliver note"
									onClick={() => moveNote(note, confirming.to)}
								>
									Deliver
								</button>
								<button className="note-action" title="Keep private" onClick={() => setConfirming(null)}>
									<i className="fa-solid fa-xmark"></i>
								</button>
							</span>
						) : (
							<span className="note-moves">
								{(LEGAL_TRANSITIONS[note.status] || []).map((to) => (
									<button
										key={to}
										className="note-move"
										title={moveLabel(note.status, to)}
										onClick={() => moveNote(note, to)}
									>
										{moveLabel(note.status, to)}
									</button>
								))}
							</span>
						)}
						{canManage && (
						<button className="note-action" title="Edit note" onClick={() => startEdit(note)}>
							<i className="fa-solid fa-pencil"></i>
						</button>
						)}
						{!canManage ? null : isConfirming(note, 'delete') ? (
							<>
								<button
									className="note-action note-action--danger"
									title="The family may have read this note"
									onClick={() => removeNote(note)}
								>
									Delete anyway
								</button>
								<button className="note-action" title="Keep it" onClick={() => setConfirming(null)}>
									<i className="fa-solid fa-xmark"></i>
								</button>
							</>
						) : (
							<button className="note-action" title="Delete note" onClick={() => removeNote(note)}>
								<i className="fa-solid fa-trash-can"></i>
							</button>
						)}
					</span>
				</>
			)}
		</li>
	);

	return (
		<div className="song-note-history">
			<div className="history-label">{label}</div>
			{actionError && <p className="note-action-error">{actionError}</p>}
			{open.length > 0 && <ul className="session-notes">{open.map(renderNote)}</ul>}
			{landed.length > 0 && (
				<>
					<div className="history-sublabel">Landed</div>
					<ul className="session-notes">{landed.map(renderNote)}</ul>
				</>
			)}
		</div>
	);
};

/**
 * Notes whose song has no repertoire row, grouped by song — Warm-ups first.
 *
 * WHY THIS EXISTS (Anthony, 2026-09-03): a note jotted on the Warm-ups tab
 * lands on the `warmups` SENTINEL, which is deliberately never a repertoire
 * doc (songNotesModel). Until this section, the profile only rendered notes
 * nested under a repertoire row, so a Warm-ups note — or one on a songId whose
 * row was later deleted — could not be seen, edited, moved or deleted anywhere
 * outside a lesson. The first report was a test note on a student with zero
 * repertoire rows, i.e. exactly the case where the tab said "No songs recorded
 * yet" over a note that existed.
 */
const groupUnfiledNotes = (notes, repertoire) => {
	const filed = new Set(repertoire.map((e) => e.id));
	const bySong = new Map();
	for (const note of notes) {
		if (filed.has(note.songId)) continue;
		const key = note.songId || 'unknown';
		if (!bySong.has(key)) bySong.set(key, { songId: key, songName: displaySongName(note), notes: [] });
		bySong.get(key).notes.push(note);
	}
	return [...bySong.values()].sort((a, b) => {
		if (a.songId === WARMUPS_SONG_ID) return -1;
		if (b.songId === WARMUPS_SONG_ID) return 1;
		return a.songName.localeCompare(b.songName);
	});
};

const StudentRepertoire = ({ studentId, studentName }) => {
	const [repertoire, setRepertoire] = useState([]);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(null);
	const [expandedSong, setExpandedSong] = useState(null);
	const [linkingSong, setLinkingSong] = useState(null);
	const [notes, setNotes] = useState([]);
	const [notesLoading, setNotesLoading] = useState(true);
	const [notesError, setNotesError] = useState('');
	const { currentUser, userProfile } = useAuth() || {};
	const byTeacher = currentUser?.uid || null;
	const canManage = canManageSongContent(userProfile);
	// Songs an admin removed (hidden, restorable; repertoireRemovalModel.js).
	const [removed, setRemoved] = useState([]);
	// { id, kind: 'remove' | 'restore' } while a remove confirm or a write is open.
	const [songAction, setSongAction] = useState(null);
	const [songActionError, setSongActionError] = useState('');
	// Library tracks (2026-09-16): the same pointer docs and the same single
	// player the Lesson Dashboard uses, so a teacher can hear a cut from the
	// profile without opening a lesson. One player for the whole list.
	const trackPlayer = useTrackPlayer();
	const studentTracks = useStudentTracks(studentId);
	// Edit tracks (2026-09-29, admins only): which song row's editor is open,
	// and the Removed tracks list's in-flight restore.
	const [editingTracks, setEditingTracks] = useState(null);
	const [trackRestore, setTrackRestore] = useState(null);
	const [trackRestoreError, setTrackRestoreError] = useState('');
	const restoreTrack = async (item) => {
		if (!canManage || trackRestore) return;
		setTrackRestore(item.file.editKey);
		setTrackRestoreError('');
		try {
			await restoreTrackFile(item.docId, item.file.editKey);
			studentTracks.reload();
		} catch (err) {
			console.error('Error restoring track:', err);
			setTrackRestoreError(err && err.message ? err.message : String(err));
		} finally {
			setTrackRestore(null);
		}
	};

	useEffect(() => {
		const fetchRepertoire = async () => {
			if (!studentId) {
				setLoading(false);
				return;
			}

			try {
				setLoading(true);
				const data = await getStudentRepertoire(studentId, { includeRemoved: true });
				setRepertoire(data.filter((e) => !e.removedAt));
				setRemoved(data.filter((e) => e.removedAt));
			} catch (err) {
				console.error('Error loading repertoire:', err);
				setError('Failed to load repertoire');
			} finally {
				setLoading(false);
			}
		};

		fetchRepertoire();
	}, [studentId]);

	// One student-wide read (index: studentId ASC, createdAt DESC — the same
	// query the Lesson Dashboard's song board runs, so it is known-served in
	// prod). Statuses are normalised once here; every row below reads them.
	useEffect(() => {
		let cancelled = false;
		if (!studentId) {
			setNotesLoading(false);
			return undefined;
		}
		setNotesLoading(true);
		fetchNotesForStudent(studentId)
			.then((rows) => {
				if (cancelled) return;
				setNotes(rows.map((n) => ({ ...n, status: mapLegacyStatus(n) })));
				setNotesError('');
			})
			.catch((err) => {
				console.error('Error loading Song Notes for student:', err);
				if (!cancelled) setNotesError('Song Notes did not load.');
			})
			.finally(() => {
				if (!cancelled) setNotesLoading(false);
			});
		return () => {
			cancelled = true;
		};
	}, [studentId]);

	const patchNote = useCallback(
		(id, patch) => setNotes((prev) => prev.map((n) => (n.id === id ? { ...n, ...patch } : n))),
		[]
	);
	const dropNote = useCallback((id) => setNotes((prev) => prev.filter((n) => n.id !== id)), []);

	const noteProps = { studentName, byTeacher, onPatch: patchNote, onRemove: dropNote, canManage };

	/** Remove (hide) a song: two taps, the second says exactly what goes. */
	const removeSong = async (entry) => {
		if (!canManage) return;
		if (!(songAction && songAction.id === entry.id && songAction.kind === 'remove')) {
			setSongActionError('');
			setSongAction({ id: entry.id, kind: 'remove' });
			return;
		}
		setSongAction({ id: entry.id, kind: 'removing' });
		try {
			await removeRepertoireEntry(entry, { uid: byTeacher, visibleEntries: repertoire });
			setRepertoire((prev) => prev.filter((e) => e.id !== entry.id));
			setRemoved((prev) => [{ ...entry, removedAt: new Date(), removedBy: byTeacher }, ...prev]);
			setNotes((prev) => prev.filter((n) => n.songId !== entry.id));
			studentTracks.reload();
			setExpandedSong(null);
			setSongAction(null);
		} catch (err) {
			console.error('Error removing song from repertoire:', err);
			setSongActionError(`${entry.songName} could not be removed. Please tell Anthony right away. (${err?.message || err})`);
			setSongAction(null);
		}
	};

	const restoreSong = async (entry) => {
		if (!canManage || songAction) return;
		setSongActionError('');
		setSongAction({ id: entry.id, kind: 'restoring' });
		try {
			await restoreRepertoireEntry(entry);
			const { removedAt, removedBy, ...back } = entry;
			setRemoved((prev) => prev.filter((e) => e.id !== entry.id));
			setRepertoire((prev) => [back, ...prev]);
			// Its notes come back with it; one re-read rather than guessing which.
			fetchNotesForStudent(studentId)
				.then((rows) => setNotes(rows.map((n) => ({ ...n, status: mapLegacyStatus(n) }))))
				.catch(() => {});
			studentTracks.reload();
			setSongAction(null);
		} catch (err) {
			console.error('Error restoring song:', err);
			setSongActionError(`${entry.songName} could not be restored. Please tell Anthony right away. (${err?.message || err})`);
			setSongAction(null);
		}
	};
	const unfiled = groupUnfiledNotes(notes, repertoire);

	const formatDate = (timestamp) => {
		if (!timestamp) return 'N/A';
		const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
		return date.toLocaleDateString('en-US', {
			month: 'short',
			day: 'numeric',
			year: 'numeric',
		});
	};

	const toggleExpanded = (songId) => {
		setExpandedSong(expandedSong === songId ? null : songId);
	};

	// The link control lives in the row's details, so opening it opens the row.
	const startLinking = (entryId) => {
		setExpandedSong(entryId);
		setLinkingSong(entryId);
	};

	/**
	 * Reflect the write locally rather than re-fetching the whole repertoire:
	 * getStudentRepertoire is the composite-index query that 400'd in prod for a
	 * day, and re-running it on every link would also collapse the row the
	 * teacher is looking at. Metadata is mirrored under the SAME rule the service
	 * writes it by — only when the row had none — so the screen cannot claim an
	 * overwrite that Firestore refused to make.
	 */
	const handleLinked = (entryId, canonicalSongId, metadata) => {
		setRepertoire((prev) =>
			prev.map((entry) =>
				entry.id === entryId
					? { ...entry, canonicalSongId, metadata: entry.metadata || metadata || null }
					: entry
			)
		);
		setLinkingSong(null);
	};

	if (loading) {
		return (
			<div className="repertoire-container">
				<div className="repertoire-header">
					<i className="fa-solid fa-music"></i>
					<span>Repertoire</span>
				</div>
				<div className="repertoire-loading">
					<i className="fa-solid fa-spinner fa-spin"></i>
					<span>Loading repertoire...</span>
				</div>
			</div>
		);
	}

	if (error) {
		return (
			<div className="repertoire-container">
				<div className="repertoire-header">
					<i className="fa-solid fa-music"></i>
					<span>Repertoire</span>
				</div>
				<div className="repertoire-error">
					<i className="fa-solid fa-exclamation-circle"></i>
					<span>{error}</span>
				</div>
			</div>
		);
	}

	return (
		<div className="repertoire-container">
			<div className="repertoire-header">
				<div className="header-left">
					<i className="fa-solid fa-music"></i>
					<span>Repertoire</span>
				</div>
				<div className="header-right">
					<span className="song-count">{repertoire.length} songs</span>
				</div>
			</div>

			{repertoire.length === 0 ? (
				<div className="repertoire-empty">
					<i className="fa-solid fa-record-vinyl"></i>
					<p>No songs recorded yet</p>
					<span className="empty-hint">
						Songs will appear here as they're worked on in lessons
					</span>
				</div>
			) : (
				<div className="repertoire-list">
					{repertoire.map((entry) => (
						<div
							key={entry.id}
							className={`repertoire-item ${expandedSong === entry.id ? 'expanded' : ''}`}
						>
							<div
								className="repertoire-item-header"
								onClick={() => toggleExpanded(entry.id)}
							>
								<div className="song-info">
									<i className="fa-solid fa-compact-disc song-icon"></i>
									<span className="song-name">{entry.songName}</span>
									{/* Canonical-song link state (question A). Explicit null =
									    unlinked — honest on screen rather than silently missing
									    from the recital join. The badge states it; the button
									    beside it resolves it (link flow, 2026-08-06). Merging
									    two already-linked songs is still its own card. */}
									{!entry.canonicalSongId && (
										<>
											<span
												className="unlinked-badge"
												title="Not linked to a canonical song yet — it won't join across students until it is"
											>
												<i className="fa-solid fa-link-slash"></i>{' '}
												<span className="unlinked-badge__text">Not linked</span>
											</span>
											<button
												type="button"
												className="link-song-open"
												onClick={(e) => {
													// The header toggles the row. Without this, clicking
													// Link on an ALREADY-OPEN row bubbles up and
													// collapses it — hiding the control in the same
													// gesture that asked for it.
													e.stopPropagation();
													startLinking(entry.id);
												}}
												aria-label={`Link ${entry.songName} to a canonical song`}
											>
												<i className="fa-solid fa-link" aria-hidden="true"></i>
												<span className="link-song-open__text">Link</span>
											</button>
										</>
									)}
								</div>
								<div className="song-meta">
									<span className="session-count" title="Times worked on">
										<i className="fa-solid fa-repeat"></i>
										{sessionCount(entry)}
									</span>
									<span className="last-worked" title="Last worked on">
										{formatDate(entry.lastWorkedOn)}
									</span>
									<i
										className={`fa-solid fa-chevron-${
											expandedSong === entry.id ? 'up' : 'down'
										} expand-icon`}
									></i>
								</div>
							</div>

							{/* Tracks sit OUTSIDE the expand toggle: a cut is one tap
							    to play, never two, and the header's own click would
							    otherwise collapse the row in the same gesture. */}
							{(() => {
								const files = tracksForSong(studentTracks.docs, entry);
								// An admin reaches the editor even when every file is
								// removed, so a song's tracks can be restored where they live.
								const editDocs = canManage ? trackDocsForSong(studentTracks.docs, entry) : [];
								if (files.length === 0 && editDocs.length === 0) return null;
								const open = editingTracks === entry.id;
								return (
									<div className="repertoire-item-tracks">
										<div className="repertoire-item-tracks__row">
											<TrackPills files={files} player={trackPlayer} compact />
											{editDocs.length > 0 && (
												<EditTracksButton open={open} onClick={() => setEditingTracks(open ? null : entry.id)} />
											)}
										</div>
										{open && (
											<TrackEditor
												docs={editDocs}
												uid={byTeacher}
												onChanged={studentTracks.reload}
												onClose={() => setEditingTracks(null)}
											/>
										)}
									</div>
								);
							})()}

							{expandedSong === entry.id && (
								<div className="repertoire-item-details">
									{linkingSong === entry.id && !entry.canonicalSongId && (
										<LinkSongControl
											entry={entry}
											onLinked={(canonicalSongId, metadata) =>
												handleLinked(entry.id, canonicalSongId, metadata)
											}
											onCancel={() => setLinkingSong(null)}
										/>
									)}

									{/* Song Metadata Section */}
									{entry.metadata && (
										<div className="song-metadata-section">
											<div className="metadata-label">
												<i className="fa-solid fa-circle-info"></i>
												Song Information
											</div>
											<div className="metadata-grid">
												{entry.metadata.artist && (
													<div className="metadata-row">
														<span className="label">Artist:</span>
														<span className="value">{entry.metadata.artist}</span>
													</div>
												)}
												{entry.metadata.composers && entry.metadata.composers.length > 0 && (
													<div className="metadata-row">
														<span className="label">Composer(s):</span>
														<span className="value">{entry.metadata.composers.join(', ')}</span>
													</div>
												)}
												{entry.metadata.arrangers && entry.metadata.arrangers.length > 0 && (
													<div className="metadata-row">
														{/* "Arranger/Orchestrator" rather than either alone: the role
														    matcher accepts "arranged by", "orchestrated by" AND
														    "orchestrator", so the stored names are a mix and naming
														    one job would misdescribe the others. */}
														<span className="label">Arranger/Orchestrator:</span>
														<span className="value">{entry.metadata.arrangers.join(', ')}</span>
													</div>
												)}
												{entry.metadata.album && (
													<div className="metadata-row">
														<span className="label">
															{entry.metadata.isMusical ? 'Show/Musical:' : 'Album:'}
														</span>
														<span className="value">{entry.metadata.album}</span>
													</div>
												)}
												{entry.metadata.releaseYear && (
													<div className="metadata-row">
														<span className="label">
															{entry.metadata.isMusical ? 'Year Written:' : 'Year:'}
														</span>
														<span className="value">{entry.metadata.releaseYear}</span>
													</div>
												)}
												{entry.metadata.genres && entry.metadata.genres.length > 0 && (
													<div className="metadata-row">
														<span className="label">Genre:</span>
														<span className="value">{entry.metadata.genres.slice(0, 3).join(', ')}</span>
													</div>
												)}
											</div>
										</div>
									)}

									<div className="details-row">
										<span className="label">First worked on:</span>
										<span className="value">{formatDate(entry.firstWorkedOn)}</span>
									</div>
									<div className="details-row">
										<span className="label">Total sessions:</span>
										<span className="value">{sessionCount(entry)}</span>
									</div>

									{/*
									  Notes live in the `songNotes` collection now — one document each, read
									  live below — rather than in the per-autosave snapshots that used to sit
									  in sessions[]. sessions[] is reduced to WHEN a song was worked.
									
									  Rendering note text from sessions[] would show text that no longer
									  matches the note it was copied from, and would sit OUTSIDE the status
									  wall — so a `captured` note could surface in a history a student reads.
									*/}
									{entry.sessions && entry.sessions.length > 0 && (
										<div className="session-history">
											<div className="history-label">Session History</div>
											<div className="sessions-list">
												{entry.sessions
													.slice()
													.reverse()
													.map((session, idx) => (
														<div key={idx} className="session-entry">
															<div className="session-date">
																<i className="fa-solid fa-calendar-day"></i>
																{session.date}
															</div>
														</div>
													))}
											</div>
										</div>
									)}

									<SongNoteHistory
										notes={notes.filter((n) => n.songId === entry.id)}
										loading={notesLoading}
										{...noteProps}
									/>

									{canManage && (
										<div className="song-remove">
											{songAction && songAction.id === entry.id && songAction.kind !== 'remove' ? (
												<span className="song-remove__busy">
													<i className="fa-solid fa-spinner fa-spin" aria-hidden="true"></i> Removing…
												</span>
											) : songAction && songAction.id === entry.id && songAction.kind === 'remove' ? (
												<span className="song-remove__confirm" role="alert">
													Remove {entry.songName}? It disappears from lessons, this profile and the family's Songs page, with its{' '}
													{notes.filter((n) => n.songId === entry.id).length} Song Notes and its tracks. Nothing is erased: you can restore it
													under Removed songs.
													<button type="button" className="note-action note-action--danger" onClick={() => removeSong(entry)}>
														Remove
													</button>
													<button type="button" className="note-action" title="Keep it" onClick={() => setSongAction(null)}>
														Keep it
													</button>
												</span>
											) : (
												<button type="button" className="song-remove__open" onClick={() => removeSong(entry)}>
													<i className="fa-solid fa-trash-can" aria-hidden="true"></i> Remove from repertoire
												</button>
											)}
										</div>
									)}
								</div>
							)}
						</div>
					))}
				</div>
			)}

			{/* Filed cuts no repertoire song claims (2026-09-24). Same predicate as
			    the per-song strip above, so a track shows in exactly one place. */}
			<TracksOnFile
				tracks={unmatchedTrackDocs(studentTracks.docs, repertoire)}
				player={trackPlayer}
				className="repertoire-tracks-on-file"
				edit={canManage ? { docs: studentTracks.docs, uid: byTeacher, onChanged: studentTracks.reload } : null}
			/>

			{canManage && removedTrackFiles(studentTracks.docs).length > 0 && (
				<section className="removed-songs" aria-label="Removed tracks">
					<div className="unfiled-notes__title">
						<i className="fa-solid fa-box-archive" aria-hidden="true"></i>
						<span>Removed tracks</span>
					</div>
					<p className="unfiled-notes__hint">
						Hidden from lessons, this profile and the family's Songs page. The files are kept. Admins only.
					</p>
					<ul className="removed-songs__list">
						{removedTrackFiles(studentTracks.docs).map((item) => (
							<li key={`${item.docId}:${item.file.editKey}`} className="removed-songs__row">
								<span className="removed-songs__name">
									{item.songName} · {item.file.length ? `${item.file.length} · ` : ''}
									{staffTrackLabel(item.file)}
								</span>
								<button
									type="button"
									className="song-remove__open"
									disabled={Boolean(trackRestore)}
									onClick={() => restoreTrack(item)}>
									{trackRestore === item.file.editKey ? (
										<>
											<i className="fa-solid fa-spinner fa-spin" aria-hidden="true"></i> Restoring…
										</>
									) : (
										<>
											<i className="fa-solid fa-rotate-left" aria-hidden="true"></i> Restore
										</>
									)}
								</button>
							</li>
						))}
					</ul>
					{trackRestoreError && (
						<p className="note-action-error" role="alert">
							That track could not be restored. Please tell Anthony right away. {trackRestoreError}
						</p>
					)}
				</section>
			)}

			{songActionError && <p className="note-action-error" role="alert">{songActionError}</p>}

			{canManage && removed.length > 0 && (
				<section className="removed-songs" aria-label="Removed songs">
					<div className="unfiled-notes__title">
						<i className="fa-solid fa-box-archive" aria-hidden="true"></i>
						<span>Removed songs</span>
					</div>
					<p className="unfiled-notes__hint">
						Hidden from lessons, this profile and the family's Songs page, with their notes and tracks. Admins only.
					</p>
					<ul className="removed-songs__list">
						{removed.map((entry) => (
							<li key={entry.id} className="removed-songs__row">
								<span className="removed-songs__name">{entry.songName}</span>
								<button
									type="button"
									className="song-remove__open"
									disabled={Boolean(songAction)}
									onClick={() => restoreSong(entry)}>
									{songAction && songAction.id === entry.id ? (
										<>
											<i className="fa-solid fa-spinner fa-spin" aria-hidden="true"></i> Restoring…
										</>
									) : (
										<>
											<i className="fa-solid fa-rotate-left" aria-hidden="true"></i> Restore
										</>
									)}
								</button>
							</li>
						))}
					</ul>
				</section>
			)}

			{notesError && <p className="note-action-error">{notesError}</p>}
			{unfiled.length > 0 && (
				<section className="unfiled-notes" aria-label="Warm-ups & other notes">
					<div className="unfiled-notes__title">
						<i className="fa-solid fa-note-sticky" aria-hidden="true"></i>
						<span>Warm-ups &amp; other notes</span>
					</div>
					<p className="unfiled-notes__hint">
						Notes on Warm-ups, and any note whose song is not in the repertoire above.
					</p>
					{unfiled.map((group) => (
						<div key={group.songId} className="unfiled-group">
							<SongNoteHistory notes={group.notes} label={group.songName} {...noteProps} />
						</div>
					))}
				</section>
			)}
		</div>
	);
};

export default StudentRepertoire;
